import { useEffect, useState, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AISignalsWebSocket, ConnectionStatus } from '@/lib/ai-signals-websocket';
import { AISignal } from '@/lib/ai-signals-api';
import { useToast } from './use-toast';

export interface UseAISignalsWebSocketReturn {
  isConnected: boolean;
  isConnecting: boolean;
  error: Error | null;
  latestSignal: AISignal | null;
  connectionStatus: ConnectionStatus;
}

export function useAISignalsWebSocket(): UseAISignalsWebSocketReturn {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [error, setError] = useState<Error | null>(null);
  const [latestSignal, setLatestSignal] = useState<AISignal | null>(null);
  const [wasDisconnected, setWasDisconnected] = useState(false);
  const wsClient = useRef<AISignalsWebSocket | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Initialize WebSocket client
  useEffect(() => {
    wsClient.current = AISignalsWebSocket.getInstance();

    // Register connection change callback
    const handleConnectionChange = (status: ConnectionStatus) => {
      setConnectionStatus(status);

      // Show toast notifications for connection status changes
      switch (status) {
        case 'connected':
          if (wasDisconnected) {
            toast({
              title: "Connected to live AI signals",
              description: "Real-time AI signals connection restored",
            });
            setWasDisconnected(false);
          }
          break;
        case 'disconnected':
          setWasDisconnected(true);
          toast({
            title: "AI backend unavailable, using cached data",
            description: "Real-time connection lost, falling back to database",
            variant: "destructive",
          });
          break;
        case 'error':
          setWasDisconnected(true);
          toast({
            title: "AI backend unavailable, using cached data",
            description: "Connection failed, using cached data from database",
            variant: "destructive",
          });
          break;
        case 'reconnecting':
          // No toast for reconnecting as per requirements
          break;
      }
    };

    // Register signal callback
    const handleSignal = (signal: AISignal) => {
      setLatestSignal(signal);

      // Update React Query cache with the new signal
      queryClient.setQueryData(['ai-signals'], (oldData: any) => {
        if (!oldData) return oldData;

        // Add the new signal to the beginning of the list
        const newSignals = [signal, ...oldData.signals.filter((s: AISignal) => s.id !== signal.id)];

        return {
          ...oldData,
          signals: newSignals,
        };
      });

      // Invalidate related queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['ai-signals'] });
      queryClient.invalidateQueries({ queryKey: ['signal-stats'] });

      // Show toast for new signal
      toast({
        title: `New ${signal.signal_type} Signal`,
        description: `${signal.symbol}: ${signal.confidence_level} confidence`,
      });
    };

    // Register error callback
    const handleError = (err: Error) => {
      setError(err);
      toast({
        title: "Signal Error",
        description: err.message,
        variant: "destructive",
      });
    };

    wsClient.current.onConnectionChange(handleConnectionChange);
    wsClient.current.onSignal(handleSignal);
    wsClient.current.on('error', handleError);

    // Connect on mount
    wsClient.current.connect();

    // Cleanup on unmount
    return () => {
      if (wsClient.current) {
        wsClient.current.removeListener('connectionChange', handleConnectionChange);
        wsClient.current.removeListener('signal', handleSignal);
        wsClient.current.removeListener('error', handleError);
        // Don't disconnect here as other components might be using the same instance
      }
    };
  }, [queryClient, toast]);

  // Fetch initial signals using React Query
  const { data: signalsData } = useQuery({
    queryKey: ['ai-signals', { websocket: true }],
    queryFn: async () => {
      // This could be used to fetch initial signals or handle fallback
      return { signals: [], total_count: 0 };
    },
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: false,
    enabled: connectionStatus === 'connected',
  });

  // Computed states
  const isConnected = connectionStatus === 'connected';
  const isConnecting = connectionStatus === 'connecting' || connectionStatus === 'reconnecting';

  return {
    isConnected,
    isConnecting,
    error,
    latestSignal,
    connectionStatus,
  };
}